import { Component } from '@theme/component'
import {
  hydrateDeferredMirrors,
  loadDeferredImagesIn,
  scheduleDeferredMirrorHydrate
} from '@theme/deferred-images'
import { debounce, observePageScrollActivity } from '@theme/utilities'

/**
 * @typedef {object} MediaBarRefs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} items
 * @property {HTMLElement} preview
 * @property {HTMLImageElement} previewImg
 * @property {HTMLElement} carouselInner
 */

/**
 * @extends {Component<MediaBarRefs>}
 */
class MediaBarComponent extends Component {
  requiredRefs = ['track', 'carouselInner']

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null

  /** @type {MediaQueryList | null} */
  #desktopMql = null

  /** @type {HTMLElement | null} */
  #activeItem = null

  /** @type {(() => void) | null} */
  #teardownScrollActivity = null

  /** @type {boolean} */
  #pageScrolling = false

  /** @type {boolean} */
  #offscreen = false

  /** @type {number | null} */
  #initTrackFrame = null

  static #VIEWPORT_PADDING = 16
  static #EFFECTIVE_OFFSET_ABOVE = 0
  static #EFFECTIVE_OFFSET_BELOW = 16

  get #previewEnabled() {
    return this.dataset.previewEnabled !== 'false'
  }

  connectedCallback() {
    super.connectedCallback()

    this.#desktopMql = window.matchMedia(this.#desktopQuery)

    // Track building (cloning to fill the viewport) is deferred to the first
    // intersection so the below-the-fold marquee does not clone off-screen on
    // engines without checkVisibility(). #setupIntersectionObserver falls back
    // to an immediate build when IntersectionObserver is unavailable.
    this.#setupIntersectionObserver()
    this.#setupScrollPause()

    this.addEventListener('pointerenter', this.#handlePointerEnter)
    this.addEventListener('pointerleave', this.#handlePointerLeave)
    this.addEventListener('focusin', this.#handleFocusIn)
    this.addEventListener('focusout', this.#handleFocusOut)

    if (this.#previewEnabled) {
      this.addEventListener('pointerover', this.#handlePointerOver)
      this.addEventListener('pointerout', this.#handlePointerOut)
    }

    window.addEventListener('resize', this.#handleResize)
    document.addEventListener(
      'deferred-image:loaded',
      this.#handleDeferredLoaded
    )
  }

  updatedCallback() {
    super.updatedCallback()
    this.#scheduleInitializeTrack()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.#initTrackFrame !== null) {
      cancelAnimationFrame(this.#initTrackFrame)
      this.#initTrackFrame = null
    }

    this.#intersectionObserver?.disconnect()
    this.#intersectionObserver = null

    this.#teardownScrollActivity?.()
    this.#teardownScrollActivity = null

    this.removeEventListener('pointerenter', this.#handlePointerEnter)
    this.removeEventListener('pointerleave', this.#handlePointerLeave)
    this.removeEventListener('focusin', this.#handleFocusIn)
    this.removeEventListener('focusout', this.#handleFocusOut)

    if (this.#previewEnabled) {
      this.removeEventListener('pointerover', this.#handlePointerOver)
      this.removeEventListener('pointerout', this.#handlePointerOut)
    }

    window.removeEventListener('resize', this.#handleResize)
    document.removeEventListener(
      'deferred-image:loaded',
      this.#handleDeferredLoaded
    )
  }

  get #desktopQuery() {
    const minWidth = Number(this.getAttribute('data-min-width')) || 769
    return `(min-width: ${minWidth}px)`
  }

  #isDesktop() {
    return (
      this.#desktopMql?.matches ?? window.matchMedia(this.#desktopQuery).matches
    )
  }

  #handleResize = debounce(() => {
    if (this.#previewEnabled) {
      this.#hidePreview()
    }
    this.#scheduleInitializeTrack()
  }, 250)

  #handleDeferredLoaded = debounce(() => {
    // Rebuild once logos swap in so clone count / half-width use real sizes.
    if (this.#offscreen) return
    this.#scheduleInitializeTrack()
  }, 150)

  #scheduleInitializeTrack() {
    if (this.#initTrackFrame !== null) {
      cancelAnimationFrame(this.#initTrackFrame)
    }

    this.#initTrackFrame = requestAnimationFrame(() => {
      this.#initTrackFrame = requestAnimationFrame(() => {
        this.#initTrackFrame = null
        this.#initializeTrack()
      })
    })
  }

  #setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      // No IO: build immediately so the marquee still renders/animates.
      this.#scheduleInitializeTrack()
      return
    }

    this.#intersectionObserver?.disconnect()
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#offscreen = !entry.isIntersecting
          this.#syncPausedState()

          if (entry.isIntersecting) {
            // Now visible/measurable: eagerly load the logos and (re)build the
            // track so clone count and half-width reflect real dimensions.
            loadDeferredImagesIn(this.refs.track)
            this.#scheduleInitializeTrack()
          }
        }
      },
      { rootMargin: '200px 0px', threshold: 0.1 }
    )

    this.#intersectionObserver.observe(this)
  }

  #setupScrollPause() {
    this.#teardownScrollActivity?.()
    this.#teardownScrollActivity = observePageScrollActivity(
      (active) => {
        this.#pageScrolling = active
        this.#syncPausedState()
      },
      { idleMs: 400 }
    )
  }

  #syncPausedState() {
    this.toggleAttribute('data-paused', this.#offscreen || this.#pageScrolling)
  }

  /**
   * @param {boolean} isHovering
   */
  #setHovering(isHovering) {
    if (isHovering) {
      this.setAttribute('data-hovering', 'true')
    } else {
      this.removeAttribute('data-hovering')
      if (this.#previewEnabled) {
        this.#hidePreview()
      }
    }
  }

  #handlePointerEnter = () => {
    if (!this.#isDesktop()) return
    this.#setHovering(true)
  }

  #handlePointerLeave = () => {
    this.#setHovering(false)
  }

  #handleFocusIn = () => {
    if (!this.#isDesktop()) return
    this.#setHovering(true)
  }

  #handleFocusOut = () => {
    this.#setHovering(false)
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerOver = (event) => {
    if (!this.#isDesktop()) return
    const item = /** @type {HTMLElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('.media-bar__item')
        : null
    )
    if (!item) return
    if (item.dataset.clone === 'true') return
    if (item === this.#activeItem) return

    this.#activeItem = item
    this.#showPreviewForItem(item)
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerOut = (event) => {
    if (!this.#isDesktop()) return

    const fromItem = /** @type {HTMLElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('.media-bar__item')
        : null
    )

    const toItem = /** @type {HTMLElement | null} */ (
      event.relatedTarget instanceof Element
        ? event.relatedTarget.closest('.media-bar__item')
        : null
    )

    if (!fromItem) return
    if (toItem) return

    this.#hidePreview()
  }

  /**
   * @param {HTMLElement} item
   */
  #showPreviewForItem(item) {
    if (!this.#previewEnabled) return

    const { preview, previewImg } = this.refs
    if (
      !(preview instanceof HTMLElement) ||
      !(previewImg instanceof HTMLImageElement)
    ) {
      return
    }

    const src = item.getAttribute('data-preview-src') || ''
    if (!src) {
      this.#hidePreview()
      return
    }

    const previewAlt = item.getAttribute('data-preview-alt') || ''

    preview.hidden = false
    preview.style.display = 'block'

    previewImg.src = src
    previewImg.alt = previewAlt

    // Position once the image has dimensions.
    if (previewImg.complete) {
      this.#positionPreview()
    } else {
      previewImg.addEventListener('load', this.#positionPreview, {
        once: true
      })
    }
  }

  #hidePreview() {
    if (!this.#previewEnabled) return

    const { preview } = this.refs
    if (!(preview instanceof HTMLElement)) return

    this.#activeItem = null
    preview.hidden = true
    preview.style.display = ''
    preview.style.top = ''
    preview.style.bottom = ''
  }

  #positionPreview = () => {
    if (!this.#previewEnabled || !this.#activeItem) return

    const { preview, previewImg } = this.refs
    if (
      !(preview instanceof HTMLElement) ||
      preview.hidden ||
      !(previewImg instanceof HTMLImageElement)
    ) {
      return
    }

    const previewHeight = previewImg.getBoundingClientRect().height
    if (!previewHeight) return

    const componentRect = this.getBoundingClientRect()
    const carouselRect = this.refs.carouselInner.getBoundingClientRect()

    const belowSpace = window.innerHeight - carouselRect.bottom
    const aboveSpace = carouselRect.top

    const { offsetAbove, offsetBelow } = this.#readPreviewOffsets()
    const canFitAbove = aboveSpace >= previewHeight + offsetAbove
    const canFitBelow = belowSpace >= previewHeight + offsetBelow

    // Prefer the side with more available space, but avoid clamping "above" to 0 (can overlap the carousel).
    const placeAbove =
      (canFitAbove && !canFitBelow) ||
      (canFitAbove && canFitBelow && belowSpace < aboveSpace) ||
      (!canFitAbove && !canFitBelow)

    const top = placeAbove
      ? carouselRect.top -
        componentRect.top -
        previewHeight -
        MediaBarComponent.#EFFECTIVE_OFFSET_ABOVE
      : carouselRect.bottom -
        componentRect.top +
        MediaBarComponent.#EFFECTIVE_OFFSET_BELOW

    if (placeAbove) {
      const minTop = MediaBarComponent.#VIEWPORT_PADDING - componentRect.top
      const maxTop =
        carouselRect.top -
        componentRect.top -
        previewHeight -
        MediaBarComponent.#VIEWPORT_PADDING
      preview.style.top = `${MediaBarComponent.#clamp(top, minTop, maxTop)}px`
    } else {
      const minTop = Math.max(0, top)
      const maxTop =
        window.innerHeight -
        componentRect.top -
        previewHeight -
        MediaBarComponent.#VIEWPORT_PADDING
      preview.style.top = `${MediaBarComponent.#clamp(minTop, 0, maxTop)}px`
    }
  }

  #readPreviewOffsets() {
    const computed = getComputedStyle(this)
    const offsetBelow =
      Number.parseFloat(
        computed.getPropertyValue('--media-bar-preview-offset-below')
      ) || 0
    const offsetAbove =
      Number.parseFloat(
        computed.getPropertyValue('--media-bar-preview-offset-above')
      ) || 0

    return { offsetAbove, offsetBelow }
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number} max
   */
  static #clamp(value, min, max) {
    const low = Math.min(min, max)
    const high = Math.max(min, max)
    return Math.min(high, Math.max(low, value))
  }

  /**
   * Whether the track can be measured reliably. Returns false while the
   * section is inside a content-visibility:auto skipped subtree (layout is
   * not computed, so clientWidth/scrollWidth are meaningless).
   * @returns {boolean}
   */
  #canMeasureTrack() {
    if (typeof this.checkVisibility === 'function') {
      if (
        !this.checkVisibility({
          contentVisibilityAuto: true,
          visibilityProperty: true
        })
      ) {
        return false
      }
    }

    return this.clientWidth > 0
  }

  #initializeTrack() {
    const { track } = this.refs

    // Defer building until layout is measurable (avoids committing a
    // mis-sized track while the section is content-visibility skipped).
    // The IntersectionObserver / deferred-image listeners re-trigger this.
    if (!this.#canMeasureTrack()) return

    // Remove previously generated nodes to avoid stacking duplicates.
    for (const node of Array.from(
      track.querySelectorAll('[data-clone], [data-repeat]')
    )) {
      node.remove()
    }

    const baseItems = Array.from(track.children).filter(
      (node) => node instanceof HTMLElement
    )

    if (baseItems.length === 0) {
      this.removeAttribute('data-ready')
      return
    }

    const isMobile = window.matchMedia('(max-width: 749px)').matches

    // Ensure the first sequence is wide enough so the loop never shows empty space.
    const maxLoops = isMobile ? 2 : 10
    let loops = 0
    const targetWidth = this.clientWidth

    while (track.scrollWidth < targetWidth && loops < maxLoops) {
      for (const item of baseItems) {
        const clone = /** @type {HTMLElement} */ (item.cloneNode(true))
        clone.setAttribute('data-repeat', 'true')
        clone.setAttribute('aria-hidden', 'true')
        this.#sanitizeClone(clone)
        this.#disableTabbing(clone)
        track.appendChild(clone)
      }
      loops += 1
    }

    // Duplicate the sequence once to create a seamless -50% loop.
    const firstHalf = Array.from(track.children).filter(
      (node) => node instanceof HTMLElement
    )

    for (const node of firstHalf) {
      const clone = /** @type {HTMLElement} */ (node.cloneNode(true))
      clone.setAttribute('data-clone', 'true')
      clone.setAttribute('aria-hidden', 'true')
      this.#sanitizeClone(clone)
      this.#disableTabbing(clone)
      track.appendChild(clone)
    }

    hydrateDeferredMirrors(track)
    scheduleDeferredMirrorHydrate(track)
    this.setAttribute('data-ready', 'true')
  }

  /**
   * Remove editor-only attributes and refs from runtime clones.
   * Also defer image loading in clones to prevent eager network requests.
   * @param {HTMLElement} root
   */
  #sanitizeClone(root) {
    this.#stripEditorAttributes(root)
    for (const el of root.querySelectorAll('*')) this.#stripEditorAttributes(el)

    // Defer cloned images to prevent duplicate eager loading
    for (const img of root.querySelectorAll('img')) {
      if (img.dataset.deferredLoaded === 'true') {
        continue
      }

      if (img.dataset.deferredSrc) {
        img.dataset.deferredMirrorSrc = img.dataset.deferredSrc
        delete img.dataset.deferredSrc
        delete img.dataset.deferredSrcset
        delete img.dataset.deferredSizes
        continue
      }

      // For non-deferred images, move src to data-src to prevent loading
      if (!img.dataset.src) {
        img.dataset.src = img.src
        img.removeAttribute('srcset')
      }
      img.src =
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
    }
  }

  /**
   * @param {Element} el
   */
  #stripEditorAttributes(el) {
    el.removeAttribute('ref')

    // Remove editor-only attributes from runtime clones.
    for (const { name } of Array.from(el.attributes)) {
      if (name.startsWith('data-shopify')) el.removeAttribute(name)
    }
  }

  /**
   * Prevent cloned items from adding duplicate tab stops (while remaining clickable).
   * @param {HTMLElement} root
   */
  #disableTabbing(root) {
    if ('tabIndex' in root) root.tabIndex = -1

    const focusable = root.querySelectorAll(
      'a, button, input, select, textarea, summary, [tabindex]'
    )

    for (const el of focusable) {
      if (!(el instanceof HTMLElement)) continue
      el.tabIndex = -1
    }
  }
}

if (!customElements.get('media-bar-component')) {
  customElements.define('media-bar-component', MediaBarComponent)
}
